import React from 'react';
import { Alert } from '../Alert';

export interface AlertConfig {
  open: boolean;
  severity: "error" | "warning" | "info" | "success";
  message: string;
  onClose: () => void;
}

interface AlertManagerProps {
  alerts: AlertConfig[];
}

export const AlertManager: React.FC<AlertManagerProps> = ({ alerts }) => {
  // Show only the first alert if there are any
  const alertToShow = alerts.length > 0 ? alerts[0] : null;
  
  return (
    <>
      {alertToShow && (
        <Alert
          key="active-alert"
          open={alertToShow.open}
          severity={alertToShow.severity}
          message={alertToShow.message}
          onClose={alertToShow.onClose}
        />
      )}
    </>
  );
};

export type AlertType = 'doneAlert' | 'successAlert' | 'lateNightAlert' | null;

export const useAlerts = () => {
  // Single state that holds the current active alert (if any)
  const [activeAlert, setActiveAlert] = React.useState<AlertType>(null);
  // 既定文言の代わりに表示する上書きメッセージ(サーバ生成の文言をそのまま出す用)
  const [messageOverride, setMessageOverride] = React.useState<string | null>(null);

  const setAlert = (alertType: Exclude<AlertType, null>, show: boolean, message?: string) => {
    if (show) {
      // Set the active alert
      setActiveAlert(alertType);
      setMessageOverride(message ?? null);
    } else if (activeAlert === alertType) {
      // Only clear if we're closing the current active alert
      setActiveAlert(null);
      setMessageOverride(null);
    }
  };

  const closeAlert = () => {
    setActiveAlert(null);
    setMessageOverride(null);
  };

  const getAlertConfigs = (): AlertConfig[] => {
    // Create a configuration for each possible alert type
    const alertMessages = {
      doneAlert: "小テストが正常に更新されました。",
      successAlert: "小テストが正常に提出されました。",
      lateNightAlert: "深夜時間帯のため、現在は動画の視聴・小テストの回答はできません。"
    };
    
    // Only return active alert if there is one
    if (activeAlert) {
      const severity = activeAlert === 'lateNightAlert' ? "error" : "success";
      return [{
        open: true,
        severity,
        message: messageOverride ?? alertMessages[activeAlert],
        onClose: closeAlert,
      }];
    }
    
    return [];
  };

  return {
    activeAlert,
    setAlert,
    closeAlert,
    getAlertConfigs,
  };
};